import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Chip,
  Tooltip,
  Autocomplete,
  Checkbox
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  QrCodeScanner as QrCodeScannerIcon,
  ArrowBack as ArrowBackIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {
  getInventoryCountRequestDetails,
  createInventoryCountEntry,
  searchProductVariantsByBarcode,
  searchProductVariantsByBarcodes,
  searchProductVariants,
  getMissingInventoryItems
} from '../../services/inventoryCountService';
import { InventoryCountRequest } from '../../types/inventoryCount';
import InventoryProductSearch from './InventoryProductSearch';

// نوع عنصر الإدخال في النموذج
interface EntryItem {
  id: string; // معرف مؤقت للعنصر في النموذج
  productId: string;
  productName: string;
  variantId: string;
  variantAttributes?: Record<string, string>;
  barcode?: string;
  quantity: number;
  unitId: string;
  unitName: string;
}

// نوع نتيجة البحث عن متغير المنتج
interface ProductVariantSearchResult {
  productId: string;
  productName: string;
  variantId: string;
  variantAttributes?: Record<string, string>;
  barcode?: string;
  unitId: string;
  unitName: string;
}

const NewInventoryCountEntry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [request, setRequest] = useState<InventoryCountRequest | null>(null);
  const [items, setItems] = useState<EntryItem[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [barcode, setBarcode] = useState<string>('');
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [openAdvancedSearchDialog, setOpenAdvancedSearchDialog] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ProductVariantSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  
  // حالة العناصر الغير مدرجة
  const [openUnlistedItemsDialog, setOpenUnlistedItemsDialog] = useState<boolean>(false);
  const [unlistedItems, setUnlistedItems] = useState<any[]>([]);
  const [loadingUnlistedItems, setLoadingUnlistedItems] = useState<boolean>(false);
  const [selectedUnlistedItem, setSelectedUnlistedItem] = useState<any>(null);
  const [unlistedItemQuantity, setUnlistedItemQuantity] = useState<number>(1);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [allItemsQuantity, setAllItemsQuantity] = useState<number>(0);

  // حالة استيراد الإكسل
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  // مرجع لحقل إدخال الباركود
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  
  // جلب تفاصيل طلب الجرد
  useEffect(() => {
    const fetchRequestDetails = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const response = await getInventoryCountRequestDetails(id);
        if (response.success) {
          setRequest(response.request);
        } else {
          setError('حدث خطأ أثناء جلب بيانات الطلب');
        }
      } catch (err) {
        console.error('Error fetching inventory count request details:', err);
        setError('حدث خطأ أثناء الاتصال بالخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [id]);

  // جلب العناصر الغير مدرجة عند فتح الحوار
  useEffect(() => {
    if (openUnlistedItemsDialog && id) {
      setLoadingUnlistedItems(true);
      getMissingInventoryItems(id)
        .then(response => {
          if (response && response.items) {
            // عرض فقط متغيرات المنتجات التي لها كمية في المستودع
            console.log("العناصر قبل الفلترة:", response.items.length);
            
            // طباعة تفاصيل العناصر للتشخيص
            response.items.forEach((item, index) => {
              console.log(`العنصر ${index + 1}:`, {
                productId: item.productId,
                productName: item.productName,
                variantId: item.variantId,
                isGenericProduct: item.isGenericProduct,
                currentQuantity: item.currentQuantity
              });
            });
            
            // فلترة لإظهار فقط متغيرات المنتجات (وليس المنتجات العامة) التي لها كمية في المستودع
            const filteredItems = response.items.filter(item => {
              // عرض العناصر التي لها variantId وليس default ولها كمية غير صفرية
              return item.variantId &&
                     item.variantId !== 'default' &&
                     !item.isGenericProduct &&
                     item.currentQuantity !== 0;
            });
            
            console.log("العناصر بعد الفلترة:", filteredItems.length);
            setUnlistedItems(filteredItems);
          } else {
            setUnlistedItems([]);
          }
        })
        .catch(err => {
          console.error('خطأ في جلب العناصر الغير مدرجة:', err);
          setError('حدث خطأ أثناء جلب العناصر الغير مدرجة');
          setUnlistedItems([]);
        })
        .finally(() => {
          setLoadingUnlistedItems(false);
        });
    }
  }, [openUnlistedItemsDialog, id]);

  // التركيز على حقل إدخال الباركود عند تفعيل المسح الضوئي
  useEffect(() => {
    if (isScanning && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [isScanning]);

  // إنشاء معرف فريد مؤقت
  const generateTempId = () => `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  // إضافة عنصر جديد إلى القائمة
  const addItem = (item: Omit<EntryItem, 'id'>) => {
    const newItem: EntryItem = {
      ...item,
      id: generateTempId()
    };
    
    setItems(prevItems => {
      // التحقق مما إذا كان العنصر موجودًا بالفعل
      const existingItemIndex = prevItems.findIndex(i => i.productId === newItem.productId && i.variantId === newItem.variantId);
      
      if (existingItemIndex >= 0) {
        // تحديث الكمية إذا كان العنصر موجودًا بالفعل
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + newItem.quantity
        };
        return updatedItems;
      } else {
        // إضافة عنصر جديد
        return [...prevItems, newItem];
      }
    });
  };

  // حذف عنصر من القائمة
  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  // تغيير كمية عنصر
  const updateItemQuantity = (id: string, quantity: number) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, quantity } : item
    ));
  };

  // البحث عن متغير المنتج بواسطة الباركود
  const handleBarcodeSearch = async (barcode: string) => {
    if (!barcode) return;
    
    try {
      console.log('البحث عن الباركود:', barcode);
      const response = await searchProductVariantsByBarcode(barcode);
      console.log('استجابة البحث بالباركود:', response);
      
      if (response.success && response.variant) {
        // التحقق من وجود بيانات المتغير المطلوبة
        if (!response.variant.productId || !response.variant.variantId) {
          console.error('بيانات المتغير غير مكتملة:', response.variant);
          setError('بيانات المنتج غير مكتملة. تفاصيل المنتج: ' +
                  (response.variant.productName || 'غير معروف'));
          setTimeout(() => setError(null), 3000);
          return;
        }
        
        // تسجيل تفاصيل المتغير للتشخيص
        console.log('تفاصيل المتغير المعثور عليه:', {
          productId: response.variant.productId,
          productName: response.variant.productName,
          variantId: response.variant.variantId,
          variantAttributes: response.variant.variantAttributes,
          barcode: response.variant.barcode,
          unitId: response.variant.unitId,
          unitName: response.variant.unitName
        });
        
        // إضافة العنصر إلى القائمة
        addItem({
          productId: response.variant.productId,
          productName: response.variant.productName,
          variantId: response.variant.variantId,
          variantAttributes: response.variant.variantAttributes,
          barcode: response.variant.barcode,
          quantity: 1, // كمية افتراضية
          unitId: response.variant.unitId,
          unitName: response.variant.unitName
        });
        
        // مسح حقل الباركود
        setBarcode('');
      } else {
        console.warn('لم يتم العثور على منتج بالباركود:', barcode, response);
        setError('لم يتم العثور على منتج بهذا الباركود');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('خطأ أثناء البحث عن المنتج بالباركود:', err);
      setError('حدث خطأ أثناء البحث عن المنتج');
      setTimeout(() => setError(null), 3000);
    }
  };


  // اختيار متغير منتج من نتائج البحث
  const handleSelectVariant = (variant: ProductVariantSearchResult) => {
    addItem({
      productId: variant.productId,
      productName: variant.productName,
      variantId: variant.variantId,
      variantAttributes: variant.variantAttributes,
      barcode: variant.barcode,
      quantity: 1, // كمية افتراضية
      unitId: variant.unitId,
      unitName: variant.unitName
    });
    
    setSearchResults([]);
  };

  // معالج اختيار متغير واحد من نافذة البحث المتقدمة
  const handleAdvancedSearchVariantSelect = (variant: any) => {
    addItem({
      productId: variant.productId,
      productName: variant.productName,
      variantId: variant.variantId,
      variantAttributes: variant.variantAttributes,
      barcode: variant.barcode,
      quantity: 1, // كمية افتراضية
      unitId: variant.unitId,
      unitName: variant.unitName
    });
    
    setOpenAdvancedSearchDialog(false);
  };

  // معالج اختيار عدة متغيرات من نافذة البحث المتقدمة
  const handleAdvancedSearchVariantsSelect = (variants: any[]) => {
    variants.forEach(variant => {
      addItem({
        productId: variant.productId,
        productName: variant.productName,
        variantId: variant.variantId,
        variantAttributes: variant.variantAttributes,
        barcode: variant.barcode,
        quantity: 1, // كمية افتراضية
        unitId: variant.unitId,
        unitName: variant.unitName
      });
    });
    
    setOpenAdvancedSearchDialog(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  // إضافة عنصر غير مدرج من المخزون
  const handleAddUnlistedItem = () => {
    if (!selectedUnlistedItem && selectedItems.size === 0) {
      setError('يجب اختيار منتج واحد على الأقل من القائمة');
      return;
    }
    
    console.log(`حالة الإضافة: selectedItems.size=${selectedItems.size}, selectedUnlistedItem=${selectedUnlistedItem ? 'موجود' : 'غير موجود'}`);
    
    // إذا كان هناك تحديد فردي
    if (selectedUnlistedItem && selectedItems.size === 0) {
      console.log(`إضافة عنصر واحد: ${selectedUnlistedItem.productName} بكمية ${unlistedItemQuantity}`);
      
      // إضافة العنصر الواحد المحدد
      addItem({
        productId: selectedUnlistedItem.productId,
        productName: selectedUnlistedItem.productName,
        variantId: selectedUnlistedItem.variantId,
        variantAttributes: selectedUnlistedItem.variantAttributes,
        barcode: selectedUnlistedItem.barcode,
        quantity: unlistedItemQuantity, // استخدام الكمية المدخلة للعنصر المحدد
        unitId: selectedUnlistedItem.unitId,
        unitName: selectedUnlistedItem.unitName
      });
    }
    // إذا كان هناك عناصر متعددة محددة
    else if (selectedItems.size > 0) {
      console.log(`*** إضافة عناصر متعددة: عدد العناصر المحددة = ${selectedItems.size} ***`);
      console.log(`الكمية المحددة للكل: ${allItemsQuantity}`);
      
      // تجميع كل العناصر المحددة في مصفوفة للإضافة
      const itemsToAdd = unlistedItems.filter(item => selectedItems.has(item.variantId));
      
      // التأكد من وجود العناصر
      console.log(`عدد العناصر المؤهلة للإضافة: ${itemsToAdd.length}`);
      
      // إنشاء نسخة جديدة من قائمة العناصر
      const updatedItems = [...items];
      
      // إضافة جميع العناصر المحددة
      itemsToAdd.forEach(item => {
        console.log(`إضافة العنصر: ${item.productName} بكمية ${allItemsQuantity}`);
        
        const newItem: EntryItem = {
          id: generateTempId(),
          productId: item.productId,
          productName: item.productName,
          variantId: item.variantId,
          variantAttributes: item.variantAttributes,
          barcode: item.barcode,
          quantity: allItemsQuantity, // استخدام الكمية الموحدة لجميع العناصر المحددة
          unitId: item.unitId,
          unitName: item.unitName
        };
        
        // التحقق مما إذا كان العنصر موجودًا بالفعل
        const existingItemIndex = updatedItems.findIndex(i => i.variantId === newItem.variantId);
        
        if (existingItemIndex >= 0) {
          // تحديث الكمية إذا كان العنصر موجودًا بالفعل
          updatedItems[existingItemIndex].quantity += newItem.quantity;
        } else {
          // إضافة عنصر جديد
          updatedItems.push(newItem);
        }
      });
      
      // تحديث القائمة بجميع العناصر دفعة واحدة
      setItems(updatedItems);
    } else {
      // لا يوجد عناصر محددة
      setError('يجب اختيار منتج واحد على الأقل من القائمة');
      return;
    }

    // إعادة تعيين الحقول وإغلاق الحوار
    setSelectedUnlistedItem(null);
    setUnlistedItemQuantity(1);
    setSelectedItems(new Set());
    setAllItemsQuantity(0);
    setOpenUnlistedItemsDialog(false);
  };

  // التبديل بين تحديد وإلغاء تحديد العنصر
  const toggleItemSelection = (item: any) => {
    const newSelection = new Set(selectedItems);
    
    if (newSelection.has(item.variantId)) {
      newSelection.delete(item.variantId);
    } else {
      newSelection.add(item.variantId);
    }
    
    setSelectedItems(newSelection);
    // إذا تم إلغاء تحديد جميع العناصر، إعادة تعيين الكمية الموحدة
    if (newSelection.size === 0) {
      setAllItemsQuantity(0);
    }
    setSelectedUnlistedItem(null); // إلغاء تحديد العنصر الفردي
  };
  // تحديد جميع العناصر
  const selectAllItems = () => {
    try {
      // إلغاء تحديد العنصر الفردي أولاً لتجنب التعارض
      setSelectedUnlistedItem(null);
      
      // الحصول على قائمة بجميع معرفات العناصر غير المدرجة
      const allIds = unlistedItems.map(item => item.variantId);
      
      console.log(`محاولة تحديد ${allIds.length} عنصر من أصل ${unlistedItems.length}`);
      
      // إنشاء مجموعة جديدة من نوع string تمامًا وإضافة كل عنصر إليها بشكل صريح
      const newSet = new Set<string>();
      for (const id of allIds) {
        newSet.add(id);
      }
      
      // تحقق من عدد العناصر في المجموعة الجديدة
      console.log(`عدد العناصر المحددة: ${newSet.size}`);
      
      // تعيين المجموعة الجديدة كعناصر محددة
      setSelectedItems(newSet);
      
      // تعيين كمية افتراضية للعناصر المحددة
      setAllItemsQuantity(0);
    } catch (error) {
      console.error("خطأ أثناء تحديد جميع العناصر:", error);
    }
  };

  // إلغاء تحديد جميع العناصر
  const deselectAllItems = () => {
    setSelectedItems(new Set());
    setAllItemsQuantity(0);
  };


  // تنفيذ البحث بالباركود عند الضغط على Enter
  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // منع السلوك الافتراضي للـ form submit
      handleBarcodeSearch(barcode);
    }
  };

  // تصدير قالب الإكسل
  const handleExportTemplate = () => {
    const templateData = [
      { 'الباركود': 'مثال: 1234567890123', 'الكمية': 'مثال: 10' },
      { 'الباركود': '', 'الكمية': '' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'قالب استيراد المنتجات');

    // تعيين عرض الأعمدة
    ws['!cols'] = [
      { wch: 20 }, // عمود الباركود
      { wch: 15 }  // عمود الكمية
    ];

    XLSX.writeFile(wb, 'قالب_استيراد_منتجات_الجرد.xlsx');
  };

  // استيراد المنتجات من الإكسل
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportErrors([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const errors: string[] = [];
      const newItems: EntryItem[] = [];

      // تجميع الصفوف وتوحيد الباركود المكرر داخل نفس الملف
      interface ParsedCountRow { rowNumber: number; barcode: string; quantity: number; }
      const parsedRows: ParsedCountRow[] = [];
      const seenBarcodes = new Map<string, ParsedCountRow>();

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i] as any;
        const rowNumber = i + 2; // +2 لأن الصف الأول هو العناوين والفهرسة تبدأ من 1

        const barcode = row['الباركود']?.toString().trim();
        const quantity = parseInt(row['الكمية']?.toString()) || 0;

        if (!barcode) {
          errors.push(`الصف ${rowNumber}: الباركود مطلوب`);
          continue;
        }

        if (quantity <= 0) {
          errors.push(`الصف ${rowNumber}: الكمية يجب أن تكون أكبر من صفر`);
          continue;
        }

        // دمج الكميات عند تكرار نفس الباركود داخل الملف
        const key = barcode.toLowerCase();
        const existingRow = seenBarcodes.get(key);
        if (existingRow) {
          existingRow.quantity += quantity;
          continue;
        }

        const parsedRow: ParsedCountRow = { rowNumber, barcode, quantity };
        seenBarcodes.set(key, parsedRow);
        parsedRows.push(parsedRow);
      }

      // بحث مجمّع واحد لكل الباركودات بدل طلب لكل صف
      const barcodeList = parsedRows.map(r => r.barcode);
      const barcodeResults = await searchProductVariantsByBarcodes(barcodeList);

      for (const parsedRow of parsedRows) {
        const match = barcodeResults.get(parsedRow.barcode.toLowerCase());

        if (!match || !match.found) {
          errors.push(`الصف ${parsedRow.rowNumber}: لم يتم العثور على منتج بالباركود ${parsedRow.barcode}`);
          continue;
        }

        // التحقق من عدم وجود المنتج مسبقاً في الصفحة
        const existingItem = items.find(item =>
          item.productId === match.productId &&
          item.variantId === match.variantId
        );

        if (existingItem) {
          errors.push(`الصف ${parsedRow.rowNumber}: المنتج موجود مسبقاً (${match.productName})`);
          continue;
        }

        // دمج الكميات إذا أشار باركودان مختلفان لنفس المنتج/المتغير داخل نفس الاستيراد
        const existingNewItem = newItems.find(item =>
          item.productId === match.productId &&
          item.variantId === match.variantId
        );

        if (existingNewItem) {
          existingNewItem.quantity += parsedRow.quantity;
          continue;
        }

        // الحفاظ على دلالة unitId كما كانت (اسم الوحدة)
        const resolvedUnitName = match.productUnitName || match.unitName || 'قطعة';

        newItems.push({
          id: `import-${Date.now()}-${newItems.length}`,
          productId: match.productId,
          productName: match.productName,
          variantId: match.variantId,
          variantAttributes: match.variantAttributes || {},
          barcode: match.mainBarcode || parsedRow.barcode,
          unitId: resolvedUnitName,
          unitName: resolvedUnitName,
          quantity: parsedRow.quantity
        });
      }

      // إضافة العناصر الجديدة
      if (newItems.length > 0) {
        setItems(prevItems => [...prevItems, ...newItems]);
      }

      setImportErrors(errors);

      if (errors.length === 0) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }

    } catch (error) {
      console.error('خطأ في استيراد الملف:', error);
      setImportErrors(['خطأ في قراءة الملف. تأكد من أن الملف بصيغة Excel صحيحة.']);
    } finally {
      setIsImporting(false);
      // إعادة تعيين قيمة input لتمكين استيراد نفس الملف مرة أخرى
      event.target.value = '';
    }
  };

  // تقديم النموذج وحفظ أمر الإدخال
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من وجود عناصر
    if (items.length === 0) {
      setError('يجب إضافة عنصر واحد على الأقل');
      return;
    }

    setSubmitting(true);
    setError(null);
    
    try {
      // تحويل العناصر إلى الصيغة المطلوبة للواجهة البرمجية
      const entryItems = items.map(item => ({
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
        unitId: item.unitId,
        unitName: item.unitName,
        barcode: item.barcode,
        productName: item.productName,
        variantAttributes: item.variantAttributes
      }));
      
      const response = await createInventoryCountEntry({
        requestId: id || '',
        items: entryItems,
        notes: notes.trim() || undefined
      });

      if (response && response.success) {
        setSuccess(true);
        // الانتقال إلى صفحة تفاصيل الطلب بعد إنشاء أمر الإدخال بنجاح
        setTimeout(() => {
          navigate(`/inventory-count/${id}`);
        }, 1500);
      } else {
        setError('حدث خطأ أثناء إنشاء أمر الإدخال');
      }
    } catch (err) {
      console.error('Error creating inventory count entry:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setSubmitting(false);
    }
  };

  // عرض حالة التحميل
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          جاري تحميل بيانات الطلب...
        </Typography>
      </Box>
    );
  }

  // عرض رسالة في حالة عدم وجود طلب
  if (!request) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        لم يتم العثور على طلب الجرد المطلوب
      </Alert>
    );
  }

  // التحقق مما إذا كان الطلب قابل للتعديل
  if (!['draft', 'in_progress'].includes(request.status)) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        لا يمكن إضافة أمر إدخال جديد. الطلب في حالة {request.status}.
      </Alert>
    );
  }

  // عرض سمات المتغير كنص
  const formatVariantAttributes = (attributes?: Record<string, string>) => {
    if (!attributes) return '';
    
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {/* رسالة النجاح */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          تم إنشاء أمر الإدخال بنجاح. جاري الانتقال إلى صفحة تفاصيل الطلب...
        </Alert>
      )}

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* أخطاء الاستيراد */}
      {importErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            تم العثور على أخطاء أثناء الاستيراد:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {importErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </Box>
        </Alert>
      )}

      {/* معلومات الطلب */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h6" gutterBottom>
          إضافة أمر إدخال جديد - طلب جرد {request.requestNumber}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          المستودع: {request.warehouseName || 'غير محدد'}
        </Typography>
      </Paper>

      {/* أدوات البحث والإدخال */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          إضافة المنتجات
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {/* أزرار البحث والإضافة */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<QrCodeScannerIcon />}
            onClick={() => setIsScanning(!isScanning)}
          >
            {isScanning ? 'إيقاف المسح الضوئي' : 'تفعيل المسح الضوئي'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SearchIcon />}
            onClick={() => setOpenAdvancedSearchDialog(true)}
          >
            البحث المتقدم عن المنتجات
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setOpenUnlistedItemsDialog(true)}
          >
            إضافة عناصر غير مدرجة
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportTemplate}
          >
            تحميل قالب الإكسل
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FileUploadIcon />}
            component="label"
            disabled={isImporting}
          >
            {isImporting ? 'جاري الاستيراد...' : 'استيراد من الإكسل'}
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
            />
          </Button>
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* مسح الباركود */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="مسح الباركود"
                fullWidth
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyPress={handleBarcodeKeyPress}
                inputRef={barcodeInputRef}
                disabled={!isScanning}
                placeholder={isScanning ? "قم بمسح الباركود..." : "انقر على زر المسح لتفعيل الماسح"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={isScanning ? "إيقاف المسح" : "تفعيل المسح"}>
                        <IconButton
                          onClick={() => setIsScanning(!isScanning)}
                          color={isScanning ? "primary" : "default"}
                        >
                          <QrCodeScannerIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

        </Grid>
      </Paper>

      {/* قائمة المنتجات المضافة */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          المنتجات المضافة
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {items.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            لم تتم إضافة أي منتجات بعد. استخدم الماسح الضوئي أو ابحث عن المنتجات لإضافتها.
          </Alert>
        ) : (
          <>
            {/* Total Quantities Summary */}
            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', mb: 2, borderRadius: 1 }}>
              <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                إجمالي الكميات: {items.reduce((total, item) => total + Math.abs(item.quantity || 0), 0)} قطعة
              </Typography>
            </Box>
            <TableContainer sx={{ mb: 2 }}>
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>اسم المنتج</TableCell>
                  <TableCell>متغير المنتج</TableCell>
                  <TableCell>الباركود</TableCell>
                  <TableCell>الوحدة</TableCell>
                  <TableCell>الكمية</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>
                      {item.variantAttributes && Object.keys(item.variantAttributes).length > 0 ?
                        formatVariantAttributes(item.variantAttributes) : '-'}
                    </TableCell>
                    <TableCell>{item.barcode || '-'}</TableCell>
                    <TableCell>{item.unitName}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          updateItemQuantity(item.id, value);
                        }}
                        size="small"
                        InputProps={{ inputProps: { } }}
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => removeItem(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* إجمالي العناصر */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Chip
            label={`إجمالي العناصر: ${items.length}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* ملاحظات */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          ملاحظات
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <TextField
          label="ملاحظات الإدخال"
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Paper>

      {/* أزرار الإجراءات */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/inventory-count/${id}`)}
        >
          رجوع
        </Button>
        
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={submitting || items.length === 0}
        >
          {submitting ? 'جاري الحفظ...' : 'حفظ أمر الإدخال'}
        </Button>
      </Box>


      {/* نافذة البحث المتقدمة عن المنتجات */}
      <InventoryProductSearch
        open={openAdvancedSearchDialog}
        onClose={() => setOpenAdvancedSearchDialog(false)}
        onSelectVariant={handleAdvancedSearchVariantSelect}
        onSelectVariants={handleAdvancedSearchVariantsSelect}
        multiSelect
        warehouseId={typeof request?.warehouse === 'string' ? request.warehouse : request?.warehouse?._id}
      />

      {/* حوار العناصر الغير مدرجة */}
      <Dialog
        open={openUnlistedItemsDialog}
        onClose={() => setOpenUnlistedItemsDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          إضافة عناصر غير مدرجة من المخزون
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            العناصر التي لها كمية (موجبة أو سالبة) في مستودع طلب الجرد ولم يتم إدراجها في أوامر إدخال سابقة
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingUnlistedItems ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : unlistedItems.length > 0 ? (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight="medium">
                  العناصر غير المدرجة في الجرد ({unlistedItems.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {selectedItems.size > 0 && (
                    <TextField
                      type="number"
                      size="small"
                      value={allItemsQuantity}
                      onChange={(e) => setAllItemsQuantity(parseInt(e.target.value) || 0)}
                      label="الكمية للعناصر المحددة"
                      sx={{ width: '180px' }}
                      autoFocus
                    />
                  )}
                  {selectedItems.size === 0 ? (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={selectAllItems}
                      startIcon={<AddIcon />}
                    >
                      تحديد الكل ({unlistedItems.length})
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleAddUnlistedItem}
                        disabled={allItemsQuantity === 0}
                      >
                        إضافة العناصر المحددة ({selectedItems.size})
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={deselectAllItems}
                      >
                        إلغاء التحديد
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  سيتم حساب الفرق تلقائياً بناءً على الكمية الحالية في المخزون.
                </Typography>
              </Box>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            checked={selectedItems.size === unlistedItems.length && unlistedItems.length > 0}
                            indeterminate={selectedItems.size > 0 && selectedItems.size < unlistedItems.length}
                            onChange={(e) => e.target.checked ? selectAllItems() : deselectAllItems()}
                          />
                          المنتج
                        </Box>
                      </TableCell>
                      <TableCell>التفاصيل</TableCell>
                      <TableCell>الكمية الحالية</TableCell>
                      <TableCell>الكمية الجديدة</TableCell>
                      <TableCell>الفرق</TableCell>
                      <TableCell>إجراء</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unlistedItems.map((item) => (
                      <TableRow
                        key={item.variantId}
                        selected={selectedUnlistedItem?.variantId === item.variantId || selectedItems.has(item.variantId)}
                        onClick={() => {
                          // إذا لم يكن هناك عناصر محددة، نقوم بتحديد هذا العنصر فردياً
                          if (selectedItems.size === 0 && !selectedUnlistedItem) {
                            setSelectedUnlistedItem(item);
                            setUnlistedItemQuantity(0);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell onClick={(e) => {
                          e.stopPropagation(); // منع انتشار الحدث
                          toggleItemSelection(item);
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={selectedItems.has(item.variantId)}
                              onChange={(e) => {
                                e.stopPropagation(); // منع انتشار الحدث
                                toggleItemSelection(item);
                              }}
                            />
                            <Typography variant="body2" fontWeight={selectedItems.has(item.variantId) ? "bold" : "normal"}>
                              {item.productName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {item.variantAttributes ? Object.entries(item.variantAttributes)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(', ') : ''}
                          {item.barcode && <div><small>باركود: {item.barcode}</small></div>}
                        </TableCell>
                        <TableCell>{item.currentQuantity} {item.unitName}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {selectedUnlistedItem?.variantId === item.variantId ? (
                            <TextField
                              type="number"
                              size="small"
                              value={unlistedItemQuantity}
                              autoFocus
                              onClick={(e) => e.stopPropagation()} // منع انتشار الحدث
                              onChange={(e) => setUnlistedItemQuantity(parseInt(e.target.value) || 0)}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">{item.unitName}</InputAdornment>,
                              }}
                            />
                          ) : selectedItems.has(item.variantId) ? (
                            <Typography variant="body2" fontWeight="bold">
                              {allItemsQuantity} {item.unitName}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {selectedUnlistedItem?.variantId === item.variantId ? (
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={(unlistedItemQuantity - item.currentQuantity) !== 0 ? "error" : "success.main"}
                            >
                              {unlistedItemQuantity - item.currentQuantity} {item.unitName}
                            </Typography>
                          ) : selectedItems.has(item.variantId) ? (
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={(allItemsQuantity - item.currentQuantity) !== 0 ? "error" : "success.main"}
                            >
                              {allItemsQuantity - item.currentQuantity} {item.unitName}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {selectedUnlistedItem?.variantId === item.variantId ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                onClick={handleAddUnlistedItem}
                              >
                                تأكيد
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setSelectedUnlistedItem(null)}
                              >
                                إلغاء
                              </Button>
                            </Box>
                          ) : selectedItems.has(item.variantId) ? (
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => toggleItemSelection(item)}
                            >
                              إلغاء التحديد
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedUnlistedItem(item);
                                setUnlistedItemQuantity(0);
                                setSelectedItems(new Set());
                              }}
                            >
                              اختيار
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info" sx={{ my: 2 }}>
              لا توجد عناصر غير مدرجة لها كمية في المستودع
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                تأكد من وجود منتجات بكميات في نفس مستودع طلب الجرد ولم يتم إدراجها في أوامر إدخال سابقة
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => {
            setOpenUnlistedItemsDialog(false);
            setSelectedItems(new Set());
            setSelectedUnlistedItem(null);
            setAllItemsQuantity(0);
          }}>
            إلغاء
          </Button>
          {selectedItems.size > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddUnlistedItem}
            >
              إضافة العناصر المحددة ({selectedItems.size})
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewInventoryCountEntry;